import React from 'react';
import { SdBin, SupplyShedDriveTime, WhpClass } from '../../../lib/types';
import { useResiliencePriorityMatrix } from '../../../hooks/useResiliencePriorityMatrix';
import { StatCard } from '../layers/StatCard';
import { WHP_PALETTE } from '../../../lib/whpColors';
import { SD_PALETTE } from '../../../lib/sdColors';
import { FO_PALETTE } from '../../../lib/foColors';
import {
  RESILIENCE_PRIORITY_PALETTE,
  resiliencePriorityTier,
} from '../../../lib/resiliencePriorityColors';
import { InsightProvenanceFooter } from './InsightProvenanceFooter';

interface Props {
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
}

const formatAcres = (acres: number): string => {
  if (acres >= 1e6) return `${(acres / 1e6).toFixed(2)}M`;
  if (acres >= 1e3) return `${(acres / 1e3).toFixed(0)}K`;
  return acres.toFixed(0);
};

// Hex (#RRGGBB) → "R, G, B" so the cell background can take a low-alpha
// rgba() tint of the tier color while leaving the acre-count text legible.
const hexToRgbTriplet = (hex: string): string => {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

// Cell background is its priority-tier color at low alpha (the matrix and
// the map paint the same cell the same hue), with magnitude reinforcement
// from a small alpha bump scaled by the cell's acre share. Tier_5 cells
// also get a bright tier_5 outline so the headline reading "top-priority
// acres" maps directly to the visually outlined cells.
const cellStyle = (
  whp: WhpClass,
  rd: SdBin,
  acres: number,
  maxAcres: number,
): React.CSSProperties => {
  const tier = resiliencePriorityTier(whp, rd);
  const triplet = hexToRgbTriplet(RESILIENCE_PRIORITY_PALETTE.colors[tier]);
  const sizeBoost = maxAcres > 0 ? acres / maxAcres : 0;
  const alpha = 0.12 + sizeBoost * 0.45;
  return {
    backgroundColor: `rgba(${triplet}, ${alpha.toFixed(3)})`,
  };
};

export const ResiliencePriorityMatrixCard: React.FC<Props> = ({ millCode, driveTime }) => {
  const { data, isLoading, error } = useResiliencePriorityMatrix(millCode, driveTime);

  if (error) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-red-500/40 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-red-400 uppercase tracking-wider">
          Insight unavailable
        </p>
        <p className="mt-2 text-body-sm font-roboto text-neutral-400">{error}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Resilience Priority Matrix
        </p>
        <p className="mt-3 text-body-sm font-roboto text-neutral-400">Computing…</p>
      </div>
    );
  }

  const maxAcres = data.matrix.reduce((m, c) => Math.max(m, c.acres), 0);
  const priorityShare =
    data.totalShedAcres > 0 ? data.ownership.priorityAcres / data.totalShedAcres : 0;
  const privateShareOfPriority =
    data.ownership.priorityAcres > 0
      ? data.ownership.privateAcres / data.ownership.priorityAcres
      : 0;

  return (
    <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card space-y-4">
      <div>
        <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
          Insight — Resilience Priority Matrix
        </p>
        <p className="mt-3 text-4xl font-roboto-mono font-bold text-figma-yellow leading-none">
          {formatAcres(data.ownership.priorityAcres)}
        </p>
        <p className="mt-1 text-body-sm font-roboto text-neutral-300">
          top-priority acres · prime thinning candidates
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="% of shed"
          value={`${(priorityShare * 100).toFixed(1)}%`}
        />
        <StatCard
          label="On private timberlands"
          value={`${(privateShareOfPriority * 100).toFixed(0)}%`}
        />
        <StatCard
          label="Private timberlands"
          value={formatAcres(data.ownership.privateAcres)}
        />
        <StatCard
          label="Federal / other"
          value={formatAcres(data.ownership.federalAcres + data.ownership.otherAcres)}
        />
      </div>

      {/* 5×4 heatmap — WHP rows × RD columns. Cell shading mirrors the
          priority-tier palette of the map overlay (low alpha so acre counts
          stay readable). Top-tier cells get a tier_5 ring matching the
          headline figure. */}
      <div>
        <p className="text-xs font-roboto text-neutral-500 mb-2">
          Acres by wildfire-hazard class × relative-density bin · cell hue = priority tier
        </p>
        <div className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-0.5 text-[10px] font-roboto-mono">
          {/* Header row — RD bins */}
          <div />
          {SD_PALETTE.order.map((bin) => (
            <div
              key={bin}
              className="flex items-center justify-center gap-1 px-1 py-1 text-neutral-400 leading-tight"
            >
              <span
                className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                style={{ backgroundColor: SD_PALETTE.colors[bin] }}
                aria-hidden="true"
              />
              <span>RD {SD_PALETTE.labels[bin]}</span>
            </div>
          ))}
          {/* Body rows — one per WHP class, highest risk at bottom so the
              heatmap reads like a traditional risk matrix (severity rises
              downward). */}
          {WHP_PALETTE.order.map((whp) => (
            <React.Fragment key={whp}>
              <div className="flex items-center gap-1.5 px-2 py-1 text-neutral-400 whitespace-nowrap">
                <span
                  className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: WHP_PALETTE.colors[whp] }}
                  aria-hidden="true"
                />
                <span>{WHP_PALETTE.labels[whp]}</span>
              </div>
              {SD_PALETTE.order.map((rd) => {
                const cell = data.matrix.find((c) => c.whpClass === whp && c.rdBin === rd);
                const acres = cell?.acres ?? 0;
                const tier = resiliencePriorityTier(whp, rd);
                const isTopTier = tier === 'tier_5';
                return (
                  <div
                    key={`${whp}:${rd}`}
                    className={`px-1 py-2 text-center rounded-sm text-neutral-200 ${isTopTier ? 'ring-1' : ''}`}
                    style={{
                      ...cellStyle(whp, rd, acres, maxAcres),
                      ...(isTopTier
                        ? { boxShadow: `inset 0 0 0 1px ${RESILIENCE_PRIORITY_PALETTE.colors.tier_5}` }
                        : {}),
                    }}
                    title={`${WHP_PALETTE.labels[whp]} × RD ${SD_PALETTE.labels[rd]} → ${RESILIENCE_PRIORITY_PALETTE.labels[tier]}: ${formatAcres(acres)} ac`}
                  >
                    {formatAcres(acres)}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <p className="mt-2 text-[10px] font-roboto text-neutral-500 italic">
          <span
            className="inline-block w-2 h-2 rounded-sm mr-1.5"
            style={{ boxShadow: `inset 0 0 0 1px ${RESILIENCE_PRIORITY_PALETTE.colors.tier_5}` }}
            aria-hidden="true"
          />
          Outlined cells map to {RESILIENCE_PRIORITY_PALETTE.labels.tier_5} in the priority matrix — these acres drive the headline figure.
        </p>
      </div>

      <InsightProvenanceFooter
        chips={[
          { color: WHP_PALETTE.colors.very_high, label: 'Wildfire Hazard Potential' },
          { color: SD_PALETTE.colors['80_plus'], label: 'Stand Density (RD)' },
          { color: FO_PALETTE.colors.family, label: 'Forest Ownership' },
        ]}
        method="Joint zonal crosstab (WHP × RD × ownership) clipped to the supply shed. Each WHP × RD cell maps to a priority tier via a documented decision matrix (lib/resiliencePriorityColors.ts) — top-tier cells are dense or overstocked stands at high or very-high fire risk. Ownership comes from USFS RDS-2025-0045 (broad classes — family, corporate, federal, state, local, tribal, unknown — at 10 m), not certification status."
        stubLabel={data.source === 'stub' ? 'Pilot preview — CSV in production pipeline' : undefined}
      />
    </div>
  );
};
